// ─────────────────────────────────────────────────────────────────────────────
// Lightweight auth/session store for the website.
//
// Mirrors the mobile app's AuthContext: after a successful login the backend
// returns { user, token, expires_at } (the token is a server session id sent as
// `Authorization: Bearer <token>`). We persist those three values in
// localStorage and expose helpers to read/clear them.
// ─────────────────────────────────────────────────────────────────────────────

export interface AuthUser {
    id: number;
    first_name?: string;
    last_name?: string;
    email?: string;
    profile_photo_url?: string | null;
    user_type?: number;
    anrede?: number;
    [key: string]: any;
}

export interface Session {
    user: AuthUser;
    token: string;
    expires_at?: string | null;
}

const TOKEN_KEY = 'me.auth.token';
const USER_KEY = 'me.auth.user';
const EXP_KEY = 'me.auth.expires_at';

const AUTH_EVENT = 'me-auth-change';

/** Notify listeners (e.g. React components) that auth state changed. */
function emitChange(): void {
    try {
        window.dispatchEvent(new Event(AUTH_EVENT));
    } catch {
        /* SSR / non-browser — ignore */
    }
}

/** Subscribe to login/logout changes. Returns an unsubscribe function. */
export function onAuthChange(listener: () => void): () => void {
    window.addEventListener(AUTH_EVENT, listener);
    window.addEventListener('storage', listener); // sync across tabs
    return () => {
        window.removeEventListener(AUTH_EVENT, listener);
        window.removeEventListener('storage', listener);
    };
}

/** True when an expiry timestamp exists and is in the past. */
function isExpired(): boolean {
    const exp = localStorage.getItem(EXP_KEY);
    if (!exp) return false; // no expiry recorded → treat as non-expiring
    const t = new Date(exp).getTime();
    if (Number.isNaN(t)) return false;
    return t <= Date.now();
}

/** The bearer token, or null when logged out / expired (auto-clears if expired). */
export function getToken(): string | null {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return null;
    if (isExpired()) {
        clearSession();
        return null;
    }
    return token;
}

export function getUser(): AuthUser | null {
    if (!getToken()) return null;
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    try {
        return JSON.parse(raw) as AuthUser;
    } catch {
        return null;
    }
}

export function isLoggedIn(): boolean {
    return getToken() !== null;
}

export function setSession(session: Session): void {
    localStorage.setItem(TOKEN_KEY, session.token);
    localStorage.setItem(USER_KEY, JSON.stringify(session.user ?? {}));
    if (session.expires_at) {
        localStorage.setItem(EXP_KEY, String(session.expires_at));
    } else {
        localStorage.removeItem(EXP_KEY);
    }
    emitChange();
}

export function clearSession(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(EXP_KEY);
    emitChange();
}

/** Authorization header object, spread-ready: `{ ...authHeader() }`. */
export function authHeader(): Record<string, string> {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
}
